import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useOrganizationId } from '../contexts/WorkspaceContext';

export type SidebarBadgeTone = 'red' | 'blue' | 'amber';

export type SidebarBadgeItemId = 'problemas' | 'acoes' | 'mesa';

export type SidebarMenuBadgeMap = Partial<Record<SidebarBadgeItemId, SidebarBadgeTone>>;

const POLL_MS = 60_000;
const RECENT_ACTION_DAYS = 7;

const TASK_NOTIFICATION_TYPES = [
  'task_assigned',
  'task_transferred',
  'task_returned',
  'task_escalated',
] as const;

async function countOpenCriticalIssues(organizationId: string, userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('issues')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .neq('status', 'closed')
    .or(`assigned_to.eq.${userId},severity.in.(critical,high)`);

  if (error) throw error;
  return count ?? 0;
}

async function countRecentAssignedActions(organizationId: string, userId: string): Promise<number> {
  const since = new Date();
  since.setDate(since.getDate() - RECENT_ACTION_DAYS);

  const { count, error } = await supabase
    .from('actions')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .eq('assigned_to', userId)
    .not('status', 'in', '(completed,cancelled)')
    .gte('created_at', since.toISOString());

  if (error) throw error;
  return count ?? 0;
}

async function countUnreadTaskNotifications(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('read', false)
    .in('type', [...TASK_NOTIFICATION_TYPES]);

  if (error) throw error;
  return count ?? 0;
}

async function countUnreadActionNotifications(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('read', false)
    .eq('type', 'action_assigned');

  if (error) throw error;
  return count ?? 0;
}

export function useSidebarMenuBadges(): SidebarMenuBadgeMap {
  const organizationId = useOrganizationId();
  const { user } = useAuth();
  const userId = user?.id;

  const problemsQuery = useQuery({
    queryKey: ['sidebar-badge', 'problemas', organizationId, userId],
    enabled: Boolean(organizationId && userId),
    staleTime: 30_000,
    refetchInterval: POLL_MS,
    meta: { suppressErrors: true },
    queryFn: () => countOpenCriticalIssues(organizationId!, userId!),
  });

  const actionsQuery = useQuery({
    queryKey: ['sidebar-badge', 'acoes', organizationId, userId],
    enabled: Boolean(organizationId && userId),
    staleTime: 30_000,
    refetchInterval: POLL_MS,
    meta: { suppressErrors: true },
    queryFn: async () => {
      const [recent, unread] = await Promise.all([
        countRecentAssignedActions(organizationId!, userId!),
        countUnreadActionNotifications(userId!),
      ]);
      return recent + unread;
    },
  });

  const tasksQuery = useQuery({
    queryKey: ['sidebar-badge', 'mesa', userId],
    enabled: Boolean(userId),
    staleTime: 30_000,
    refetchInterval: POLL_MS,
    meta: { suppressErrors: true },
    queryFn: () => countUnreadTaskNotifications(userId!),
  });

  const badges: SidebarMenuBadgeMap = {};

  if ((problemsQuery.data ?? 0) > 0) {
    badges.problemas = 'red';
  }

  if ((actionsQuery.data ?? 0) > 0) {
    badges.acoes = 'blue';
  }

  if ((tasksQuery.data ?? 0) > 0) {
    badges.mesa = 'amber';
  }

  return badges;
}
